import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  latestRefreshToken,
  setTokens,
  type Tokens,
} from './session'

/**
 * The HTTP client. Plain `fetch` rather than a library, because the one piece of
 * real logic here — single-flight refresh with correct replay of the original
 * request — is clearer written out than assembled from interceptors.
 */

/**
 * Empty by default, so requests stay same-origin: the dev server proxies /api
 * and production sits behind one ingress. Set VITE_API_URL only when the API
 * genuinely lives on another origin.
 */
const BASE_URL = `${import.meta.env.VITE_API_URL ?? ''}/api/v1`

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown }
  requestId?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown
  readonly requestId: string | undefined

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback)
    this.name = 'ApiError'
    this.status = status
    this.code = body?.error?.code ?? 'UNKNOWN'
    this.details = body?.error?.details
    this.requestId = body?.requestId
  }
}

/** A network failure, a CORS rejection, or the user being offline. */
export class NetworkError extends Error {
  constructor() {
    super('network_unavailable')
    this.name = 'NetworkError'
  }
}

let refreshInFlight: Promise<boolean> | null = null

/**
 * The name every tab of this origin queues on before rotating the refresh
 * token. The token is shared through localStorage, and the server treats a
 * second presentation of a consumed token as theft: two tabs restored at once
 * — Safari bringing the app back, a duplicated tab, a reload of both — used to
 * refresh together, the second one tripped reuse detection, and both were
 * signed out with no explanation.
 */
const REFRESH_LOCK = 'auth-refresh'

/** localStorage stand-in for the lock where Web Locks are missing. */
const REFRESH_LOCK_KEY = 'auth_refresh_lock'
const LOCK_HOLD_MS = 10_000
const LOCK_WAIT_MS = 8_000

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Runs `task` while no other tab of this origin runs one.
 *
 * Web Locks are the real thing: granted in order, released when the promise
 * settles, dropped if the tab dies. Without them (old WebKit, some WebViews)
 * a lease in localStorage stands in: claim it, wait a beat for a rival claim to
 * land, and run only if the claim held. Where even storage is unavailable the
 * token is not shared between tabs either, so there is nothing to serialise.
 */
async function withCrossTabLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (locks !== undefined && typeof locks.request === 'function') {
    return locks.request(REFRESH_LOCK, task)
  }
  return withStorageLease(task)
}

async function withStorageLease<T>(task: () => Promise<T>): Promise<T> {
  let storage: Storage
  try {
    storage = localStorage
    storage.getItem(REFRESH_LOCK_KEY)
  } catch {
    return task()
  }

  const claim = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  const giveUpAt = Date.now() + LOCK_WAIT_MS
  while (Date.now() < giveUpAt) {
    const held = storage.getItem(REFRESH_LOCK_KEY)
    const expires = held === null ? 0 : Number(held.split(':')[1])
    if (held === null || !(expires > Date.now())) {
      storage.setItem(REFRESH_LOCK_KEY, `${claim}:${Date.now() + LOCK_HOLD_MS}`)
      await sleep(25)
      if (storage.getItem(REFRESH_LOCK_KEY)?.startsWith(`${claim}:`)) {
        try {
          return await task()
        } finally {
          if (storage.getItem(REFRESH_LOCK_KEY)?.startsWith(`${claim}:`)) {
            storage.removeItem(REFRESH_LOCK_KEY)
          }
        }
      }
    }
    await sleep(40 + Math.random() * 60)
  }
  // Waited long enough: the holder is probably a tab that never released. The
  // server's rotation grace window is the last line of defence from here.
  return task()
}

async function performRefresh(): Promise<boolean> {
  if (getRefreshToken() === null) return false
  return withCrossTabLock(rotateRefreshToken)
}

async function rotateRefreshToken(): Promise<boolean> {
  // Another tab may have rotated while this one waited for the lock. Present
  // what storage holds now — never the copy taken before the wait, which the
  // server would rightly read as a replay. Gone from storage means another
  // tab signed out, and so has this one.
  const token = latestRefreshToken()
  if (token === null) return false

  // The installation id binds the server's two-tab grace window: a replay of a
  // just-rotated token is answered with its successor only when it comes from
  // the installation that rotated it. Tabs share storage, so they share the id.
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  const device = deviceId()
  if (device !== null) headers['x-device-id'] = device

  try {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken: token }),
    })

    if (!response.ok) {
      // Only a verdict on the token itself ends the session: a replayed or
      // revoked refresh token (401/403) means the family is gone and retrying
      // would only revoke it again. A throttled (429) or failing (5xx) API is
      // the API's problem, not the user's — keep the token and try later,
      // exactly as a network blip is treated below.
      if (response.status === 401 || response.status === 403) clearTokens()
      return false
    }

    /*
     * The API wraps the pair: `{ tokens: { accessToken, refreshToken } }`.
     * Casting the whole body to `Tokens` left both fields undefined, which sent
     * `Bearer undefined` on the next request, wrote the string "undefined" into
     * localStorage, and made the refresh after that arrive with an empty body —
     * so every session died fifteen minutes in, silently, and the user was
     * bounced to the sign-in screen with no explanation.
     */
    const body = (await response.json()) as { tokens?: Tokens } | Tokens
    const tokens = 'tokens' in body && body.tokens !== undefined ? body.tokens : (body as Tokens)

    if (typeof tokens.accessToken !== 'string' || typeof tokens.refreshToken !== 'string') {
      // Fail loudly rather than storing undefined and dying one request later.
      clearTokens()
      return false
    }

    setTokens(tokens)
    return true
  } catch {
    // A network blip must not sign the user out.
    return false
  }
}

/** One rotation at a time in this tab; every caller awaits the same promise. */
function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/**
 * A stable id for this installation, minted once and kept in storage. The
 * server records it at registration so the partner programme can tell a
 * second account from the same phone; it is never used to identify a person.
 */
const DEVICE_KEY = 'device:id'
let deviceIdCache: string | null | undefined
export function deviceId(): string | null {
  if (deviceIdCache !== undefined) return deviceIdCache
  try {
    let id = localStorage.getItem(DEVICE_KEY)
    if (id === null || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
      id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2)).replace(/-/g, '')
      localStorage.setItem(DEVICE_KEY, id)
    }
    deviceIdCache = id
  } catch {
    deviceIdCache = null
  }
  return deviceIdCache
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
  /** Skips the Authorization header, for the auth endpoints themselves. */
  anonymous?: boolean
  /**
   * Sent as `Idempotency-Key`. Every request that moves money carries one, so
   * that a response lost in transit can be retried without the server treating
   * the retry as a second withdrawal, transfer, subscription or order. The key
   * belongs to the *user's decision*, not to the attempt: it is generated once
   * when the action is confirmed and reused for every retry of that one
   * confirmation. See `newIdempotencyKey`.
   */
  idempotencyKey?: string
  /** The user's one-time code, sent as `X-TOTP-Code` for actions their second factor guards. */
  totpCode?: string
}

/**
 * A fresh key for one confirmed money-moving action.
 *
 * `crypto.randomUUID` needs a secure context, which a phone on plain HTTP in
 * testing does not have — and a missing key would silently turn the retry
 * button back into a way to send the money twice, so there is a fallback rather
 * than a throw.
 */
export function newIdempotencyKey(): string {
  const webCrypto = globalThis.crypto as Crypto | undefined
  if (webCrypto !== undefined && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }

  if (webCrypto !== undefined && typeof webCrypto.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }

  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  if (!query) return BASE_URL + path

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value))
  }
  const qs = params.toString()
  return qs === '' ? BASE_URL + path : `${BASE_URL}${path}?${qs}`
}

async function send(path: string, options: RequestOptions, retrying: boolean): Promise<Response> {
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  // The same key survives the transparent 401 replay below, which is itself a
  // second delivery of the same request.
  if (options.idempotencyKey !== undefined) headers['idempotency-key'] = options.idempotencyKey
  if (options.totpCode !== undefined) headers['x-totp-code'] = options.totpCode
  const device = deviceId()
  if (device !== null) headers['x-device-id'] = device

  let token = getAccessToken()
  // A fresh page load has a refresh token and no access token: rotating first
  // saves the 401 round trip (and the 401 in the server log) that every
  // screen otherwise starts with.
  if (!options.anonymous && token === null && !retrying && getRefreshToken() !== null) {
    if (await refreshOnce()) token = getAccessToken()
  }
  if (!options.anonymous && token !== null) headers.authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch {
    throw new NetworkError()
  }

  if (response.status === 401 && !options.anonymous && !retrying) {
    const refreshed = await refreshOnce()
    if (refreshed) return send(path, options, true)
  }

  return response
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options, false)

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const parsed: unknown = text === '' ? null : JSON.parse(text)

  if (!response.ok) {
    throw new ApiError(response.status, parsed as ApiErrorBody | null, response.statusText)
  }
  return parsed as T
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE', body }),
}
